import { styled } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import { format, parseISO } from 'date-fns';
import { GRID_DATE_PICKER_POPPER_CLASS } from './datePickerConstants';
import { usePickerOpenCoordinator } from './usePickerOpenCoordinator';

const StyledDatePicker = styled(DatePicker, {
  shouldForwardProp: (prop) => prop !== 'isEditableText',
})<{ isEditableText?: boolean }>(({ theme, isEditableText }) =>
  isEditableText
    ? {
        '& .MuiInputBase-root input': {
          padding: '8.5px 14px',
          paddingLeft: theme.spacing(0.5),
          display: 'flex',
          alignItems: 'center',
        },
        '.MuiInputBase-root': {
          marginLeft: theme.spacing(-1),
          paddingLeft: theme.spacing(0.5),
          width: '100%',
        },
        '.MuiInputBase-input': {
          paddingLeft: theme.spacing(0.5),
        },
        '.MuiInputBase-input::-webkit-input-placeholder': {
          visibility: 'hidden',
        },
        '&:hover .MuiInputBase-input::-webkit-input-placeholder': {
          visibility: 'visible',
        },
        '& fieldset': {
          opacity: 0,
        },
        '&:hover fieldset': {
          opacity: 1,
        },
        '& .MuiInputAdornment-root': {
          display: 'none',
        },
        '.Mui-focused ': {
          ' & .MuiOutlinedInput-notchedOutline': {
            opacity: 1,
          },
        },
      }
    : {
        '& .MuiPickersInputBase-root': { paddingLeft: 0 },
        '& .MuiPickersSectionList-root': { padding: '8.5px 14px' },
      },
);

export const customDatetoIsoDateString = (date: Date | undefined | null) => {
  if (!date) {
    return undefined;
  }

  return format(date, 'yyyy-MM-dd');
};

export const DateFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  isEditableText,
  onClose,
  ...rest
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
  onClose?: () => void;
}) => {
  const coordinator = usePickerOpenCoordinator(rest.autoFocus);

  if (fieldConfiguration.fieldType !== 'DATE') return;

  const handleClose = () => {
    coordinator.onClose();
    onClose?.();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledDatePicker
        className="fs-exclude"
        // This magic is needed to get a data-testid properly assigned
        slotProps={{
          textField: {
            inputProps: {
              'data-testid': 'fim3-custom-field-input-date',
              'data-dd-action-name': 'FIM 3 Custom Field - date picker redacted',
            },
          },
          popper: { className: GRID_DATE_PICKER_POPPER_CLASS },
          // In grid-edit mode the calendar portals to body while the cell
          // wrapper manages focus on its own. Without this, MUI's focus trap
          // fights the grid for focus and overflows the call stack.
          desktopTrapFocus: { disableEnforceFocus: isEditableText },
        }}
        isEditableText={isEditableText}
        value={values[0] ? parseISO(values[0]) : null}
        onChange={(newValue) => {
          const newValues = [customDatetoIsoDateString(newValue) ?? ''];
          setValues(newValues);
        }}
        {...rest}
        open={coordinator.open}
        onOpen={coordinator.onOpen}
        onClose={handleClose}
      />
    </LocalizationProvider>
  );
};
